/**
 * Reusable viewer mount.
 *
 * Two delivery modes from one entry point:
 *   - Standalone fullscreen page (src/main.ts) — calls mount() against the
 *     page's pre-built #viewer-root.
 *   - Embed (any host site, e.g. Astro) — caller provides a container
 *     element; mount() creates the canvas + UI inside it and returns a
 *     handle so the host can configure, listen, destroy.
 *
 * Usage from the Astro site:
 *
 *   import { mount } from '@olloport/viewer2';
 *
 *   const handle = await mount({
 *       container: document.getElementById('hero-viewer'),
 *       sceneUrl: '/scenes/gallery/scene.json',
 *       ui: { fps: false, quality: false }     // chrome off for hero usage
 *   });
 *
 *   // later
 *   handle.destroy();
 *
 * Iframe embed is even simpler — just point an <iframe src="..."> at the
 * standalone page.
 */

import { FILLMODE_NONE, RESOLUTION_AUTO, Vec3 } from 'playcanvas';

import { ViewerApp } from './app';
import { readSceneRect, sceneTargetOf } from './capture/frame-source';
import { getLodFloor, setLodFloor } from './scene/lod-clamp';
import { OrbitController } from './controls/orbit-controller';
import { WalkController } from './controls/walk-controller';
import {
    applyScheme, DEFAULT_SCHEME, isTouchDevice, registryFor, schemeHintRows, schemeMode, schemesEligible,
    type SchemeContext, type SchemeId
} from './experiments/control-schemes';
import { SchemePicker } from './experiments/scheme-picker';
import { feedbackEndpoint, sendFeedback } from './experiments/feedback';
import { AdaptiveQualityController } from './quality/adaptive-quality';
import { detectDevice } from './quality/device-class';
import { applyEffects, collectEffects } from './scene/apply-effects';
import { applyLighting, applyLightingNative, lightingNeedsCompose } from './scene/apply-lighting';
import { loadScene, type LoadedScene } from './scene/scene-loader';
import { Branding } from './ui/branding';
import { CameraTour } from './ui/camera-tour';
import { ControlsHint, type HintMode, type HintProbe, type HintRow } from './ui/controls-hint';
import { DemoControls, demoChoicesFor } from './ui/demo-controls';
import { DemoTutorial, type TutorialGestures } from './ui/demo-tutorial';
import { expectedLoadMs, recordLoad, startStallMeter } from './demo/load-clock';
import { FpsOverlay } from './ui/fps-overlay';
import { InfoPanel } from './ui/info-panel';
import { LoadingScreen } from './ui/loading-screen';
import { LoadDiagnostics } from './ui/diagnostics';
import { MarkersOverlay } from './ui/markers-overlay';
import { ICON_RULER, MeasureTool } from './ui/measure-tool';
import { GUIDE_LABEL, GUIDE_SHORT, ICON_HELP, OnboardingGuide } from './ui/onboarding-guide';
import { resolveLang } from './ui/lang';
import { QualityDropdown } from './ui/quality-dropdown';
import { QualityPicker, SettingsPanel, savedQuality } from './ui/settings-panel';
import { DemoMenu, isPhoneScreen } from './ui/demo-menu';
import { flythroughUrl, orientation, recordFlythrough, waitViewSettled, type Orientation, type RecordProgress } from './demo/flythrough-video';
import type { QualityMode } from './quality/adaptive-quality';
import { Toolbar } from './ui/toolbar';
import { VrMode } from './xr/vr-mode';
import { buildWalkSurfaces, raycastWalkSurfaces } from './walk-geometry';
import { loadVoxelGrid } from '../../shared/scene-core/voxel-grid';
import { VoxelCollider } from '../../shared/scene-core/voxel-walk';

export interface MountOptions {
    /** Where to mount. Must have a non-zero size at mount time. */
    container: HTMLElement;
    /**
     * Absolute or relative URL to the scene.json. Required — when embedded,
     * the host site knows what to load; we don't sniff window.location.
     */
    sceneUrl: string;
    /**
     * Toggle individual UI chrome. Defaults: loadingScreen/toolbar/info/
     * branding ON, fps/quality OFF (dev knobs, off by default for end users).
     */
    ui?: {
        loadingScreen?: boolean;
        toolbar?: boolean;
        info?: boolean;
        branding?: boolean;
        fps?: boolean;
        quality?: boolean;
        /** Controls hint card (bottom-centre); auto-dismisses. */
        hints?: boolean;
    };
    /** Treat as embedded (compact chrome, no expand on info, dimmer branding). */
    embed?: boolean;
    /**
     * Stand demo (demo.html → ?demo=1): start on the top quality rung, show
     * the control pills, a Scenes button in the toolbar and the quality
     * dropdown; send no load telemetry. Nothing else changes.
     */
    demo?: boolean;
    /** Demo: the Scenes button calls this instead of leaving the page. */
    onScenes?: () => void;
    /** Demo: the loader's video, kept loaded by the page between opens. */
    loaderVideo?: HTMLVideoElement | null;
    /** Demo Setup: record this scene's flythrough once the loader is gone
     *  (demo/flythrough-video.ts), then report through onDone. */
    record?: {
        signal: AbortSignal;
        onProgress: (p: RecordProgress) => void;
        onDone: (err: unknown, result?: { bytes: number; frames: number; capped: number }) => void;
    };
}

export interface ViewerHandle {
    app: ViewerApp;
    scene: LoadedScene | null;
    quality: AdaptiveQualityController | null;
    /** Release all GPU + DOM resources. After this, calling mount again is safe. */
    destroy(): void;
}

/** What a scene request is worth in the denominator before the server has
 *  told us (no Content-Length — Cloudflare strips it when it brotli-compresses
 *  JSON on the fly). Deliberately on the high side: an over-estimate makes the
 *  bar arrive early-and-slow, an under-estimate makes it race to ~100% while
 *  bytes still flow — the old "reads as complete the whole way down" bug.
 *  Corrected to the real byte count the moment the request settles. */
function estimateBytes(url: string): number {
    if (/lod-meta\.json($|[?#])/i.test(url)) return 900_000;   // 456KB (galeria) – 1.3MB (cartoixa)
    if (/(^|\/)scene\.json($|[?#])/i.test(url)) return 4_000;
    if (/meta\.json($|[?#])/i.test(url)) return 8_000;         // per-chunk headers
    return 200_000;
}

interface TrackedRequest {
    url: string;
    loaded: number;
    /** Server-declared Content-Length, 0 when undeclared. */
    total: number;
    estimate: number;
    done: boolean;
}

interface ProgressSnapshot {
    /** Σloaded / Σbest-known-total over every scene request seen so far. */
    raw: number;
    /** Σ bytes received so far — a monotonic activity signal. */
    loadedBytes: number;
    /** Scene requests currently in flight (started, not settled). */
    inflight: number;
    /** Pipeline-stage signals, used by the caller to hold the bar back while
     *  work we KNOW is coming has not shown up in the denominator yet. */
    onlySceneJson: boolean;
    lodMetaSeen: boolean;
    chunkSeen: boolean;
    /** Per-LOD-tier chunk request counts, keyed by tier index. The octree
     *  names chunk directories `<tier>_<chunk>/`, and HIGHER index = COARSER,
     *  so the first tier we ever see requested is the coarse one the engine
     *  loads first. This is the only handle we get on "which detail level is
     *  actually resident" — the engine exposes no public API for it. */
    tiers: Map<number, { done: number; inflight: number }>;
}

/** `.../3_12/meta.json` or `.../3_12/sh0.webp` -> 3. */
function chunkTier(url: string): number | null {
    const m = /\/(\d+)_\d+\//.exec(url);
    return m ? Number(m[1]) : null;
}

/**
 * Track REAL scene download progress by observing the network the engine
 * actually drives. PlayCanvas loads assets through XMLHttpRequest and some
 * paths through fetch — hook both (scoped to scene-asset urls) and report a
 * byte-ratio snapshot on every progress event. Undeclared response sizes get
 * a byte estimate (never their own loaded count — that made them read as 100%
 * complete the whole way down). Returns a disposer that unhooks everything.
 */
function trackSceneDownloads(
    isSceneUrl: (url: string) => boolean,
    onUpdate: (s: ProgressSnapshot) => void
): () => void {
    const per = new Map<object, TrackedRequest>();
    let disposed = false;

    const isLodMetaUrl = (u: string) => /lod-meta\.json($|[?#])/i.test(u);
    const isSceneJsonUrl = (u: string) => /(^|\/)scene\.json($|[?#])/i.test(u);

    const report = () => {
        if (disposed) return;
        let loaded = 0;
        let total = 0;
        let inflight = 0;
        let lodMetaSeen = false;
        let chunkSeen = false;
        const tiers = new Map<number, { done: number; inflight: number }>();
        for (const s of per.values()) {
            const tier = chunkTier(s.url);
            if (tier !== null) {
                const t = tiers.get(tier) ?? { done: 0, inflight: 0 };
                if (s.done) t.done++; else t.inflight++;
                tiers.set(tier, t);
            }
            loaded += s.loaded;
            total += s.done
                ? s.loaded
                : (s.total > 0 ? Math.max(s.total, s.loaded) : Math.max(s.estimate, s.loaded));
            if (!s.done) inflight++;
            if (isLodMetaUrl(s.url)) lodMetaSeen = true;
            else if (!isSceneJsonUrl(s.url)) chunkSeen = true;
        }
        onUpdate({
            raw: total > 0 ? Math.min(1, loaded / total) : 0,
            loadedBytes: loaded,
            inflight,
            onlySceneJson: !lodMetaSeen && !chunkSeen,
            lodMetaSeen,
            chunkSeen,
            tiers
        });
    };

    const track = (url: string): object => {
        const key = {};
        per.set(key, { url, loaded: 0, total: 0, estimate: estimateBytes(url), done: false });
        return key;
    };

    // ── XHR (PlayCanvas Http) ───────────────────────────────────────────
    const XHR = XMLHttpRequest.prototype;
    const origOpen = XHR.open;
    const origSend = XHR.send;
    type Tagged = XMLHttpRequest & { __olloSceneUrl?: string };
    XHR.open = function (this: Tagged, method: string, url: string | URL, ...rest: unknown[]) {
        const u = String(url);
        this.__olloSceneUrl = isSceneUrl(u) ? u : undefined;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (origOpen as any).call(this, method, url, ...rest);
    };
    XHR.send = function (this: Tagged, ...args: unknown[]) {
        if (this.__olloSceneUrl) {
            const key = track(this.__olloSceneUrl);
            this.addEventListener('progress', (e) => {
                const s = per.get(key);
                if (!s) return;
                s.loaded = e.loaded;
                if (e.lengthComputable) s.total = e.total;
                report();
            });
            const settle = () => {
                const s = per.get(key);
                if (!s) return;
                s.done = true;
                if (s.total === 0) s.total = s.loaded;
                s.loaded = Math.max(s.loaded, s.total);
                report();
            };
            this.addEventListener('loadend', settle);
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (origSend as any).apply(this, args);
    };

    // ── fetch ───────────────────────────────────────────────────────────
    const origFetch = window.fetch;
    window.fetch = async function (input: RequestInfo | URL, init?: RequestInit) {
        const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
        if (!isSceneUrl(url)) return origFetch.call(this, input, init);
        const key = track(url);
        try {
            const res = await origFetch.call(this, input, init);
            const s = per.get(key);
            if (s) {
                const len = Number(res.headers.get('Content-Length') || 0);
                if (len > 0) s.total = len;
            }
            if (!res.body) {
                const s2 = per.get(key);
                if (s2) { s2.done = true; s2.loaded = Math.max(s2.loaded, s2.total); }
                report();
                return res;
            }
            // Tee so we can count bytes without consuming the caller's stream.
            const [a, b] = res.body.tee();
            (async () => {
                const reader = b.getReader();
                try {
                    for (;;) {
                        const { done, value } = await reader.read();
                        const s3 = per.get(key);
                        if (done) {
                            if (s3) { s3.done = true; if (s3.total === 0) s3.total = s3.loaded; }
                            report();
                            break;
                        }
                        if (s3 && value) { s3.loaded += value.byteLength; report(); }
                    }
                } catch { /* stream aborted — leave last counts */ }
            })();
            return new Response(a, { status: res.status, statusText: res.statusText, headers: res.headers });
        } catch (err) {
            const s = per.get(key);
            if (s) { s.done = true; }
            report();
            throw err;
        }
    };

    return () => {
        disposed = true;
        XHR.open = origOpen;
        XHR.send = origSend;
        window.fetch = origFetch;
    };
}

/**
 * Resolve once the splat has ACTUALLY painted — with FINISHED content, not a
 * wash. Probes three 16px rows of the backbuffer (30% / 50% / 70% of width,
 * centre height) each ~10th frame inside postrender, while the buffer is
 * valid. Three conditions, two consecutive probes:
 *   - lit: pixels meaningfully brighter than the near-black clear colour;
 *   - edges: high-frequency detail — adjacent-pixel jumps. While chunk
 *     textures are half-applied the engine renders a screen-filling grey
 *     wash of giant gaussians (positions in, scales/colours pending). On a
 *     real GPU that wash is a SOFT GRADIENT, so a global-variance check
 *     passes it (that was the mobile grey) — but its footprints span
 *     hundreds of pixels, so short-stride deltas stay tiny. Real content
 *     has contrast at 8px stride everywhere, and is lit unevenly across
 *     the frame (the wash is one flat value). NOT adjacent 1px deltas:
 *     splats are smooth, and that test failed on a whole apartment.
 *   - still: the probed pixels match the previous probe. The camera is
 *     static until the reveal (nav attaches after the gate), so a frame
 *     that is still morphing = chunks still applying.
 * This is ground truth at the pixel level — frame counting, timers, and the
 * engine's own pending counters have all lied here.
 *
 * The escape hatch is STAGNATION-based, not wall-clock: give up only after
 * the frame has sat unchanged-and-contentless for `stagnantMs`. A wall-clock
 * cap here revealed the wash on slow decoders (iPhone Chrome's WebGL is
 * markedly slower than Safari's — the decode simply outlived the old 12s
 * cap while still actively progressing). While probes keep CHANGING, decode
 * is alive and the reveal keeps waiting; an absolute backstop guards the
 * truly pathological.
 *
 * ...but a still frame is NOT evidence of a dead load. Before nothing has
 * decoded yet the frame is legitimately static — clear colour, no content,
 * zero drift — while chunks stream in perfectly healthily. On a fast desktop
 * that window is shorter than `stagnantMs` so it never showed; on a phone on
 * mobile data it is routinely longer, and the probe then resolved having
 * proven NOTHING. That silently demoted the reveal to the settle heuristic
 * alone — the very heuristic the pixel probe exists to backstop, and the one
 * that admits it can read ready=true mid-wash. Result: grey screen on the
 * device, never in the lab. So network progress counts as activity too: while
 * scene bytes are still arriving, this probe does not get to give up.
 * `netActivityAt` is the byte tracker's last-progress timestamp, fed by XHR
 * progress events, so it advances mid-request and not just on completion.
 *
 * Resolution reasons are recorded on `window.__revealReason` — the previous
 * two iOS rounds were spent guessing which gate let go, and a device test
 * that reports its own answer is worth more than another round of inference.
 */
function waitForSplatPainted(
    app: pc.AppBase,
    netActivityAt: () => number,
    onSlow: () => void,
    // True when no scene requests are in flight — together with a cold
    // netActivityAt this means the engine has nothing left it WANTS to fetch.
    isNetIdle: () => boolean,
    // Ask the loader to release one finer LOD tier (steps the boot clamp
    // down, which is what makes the engine fetch it). Returns false once
    // there is nothing finer left to ask for.
    onStarved: () => boolean,
    // Stagnation with a picture that never showed structure: the connection
    // is dead. Tell the loading screen to say so instead of revealing.
    onDead: () => void,
    // Give-up threshold, and it must mean DEAD, not slow: both clocks cold, no
    // frame change AND no scene bytes, for this long. It was 8s, which a phone
    // clears routinely before the first chunk decodes — and giving up here does
    // not just skip a check, it reveals an unpainted scene, i.e. the grey
    // screen. Long enough that a merely-slow load can never reach it; short
    // enough that a genuinely broken scene (or a start view the pixel probe can
    // never satisfy) does not hold the loader hostage.
    stagnantMs = 45000,
    // Late enough that an ordinary phone load never trips it (a big scene on
    // mobile data can legitimately take ~20s), early enough that the bar's tail
    // is never mute for long — the silence there is what read as a hang.
    slowMs = 25000,
    // Last resort only, NOT a load-time budget. It used to be 45s, which on a
    // phone on mobile data is an ordinary time for a scene to still be
    // streaming — so the cap fired mid-load and revealed the wash. That is the
    // grey screen: not a rendering bug, a deadline shorter than the download.
    // Progress-based escapes (stagnation above, the wedge in the settle gate)
    // are what release a genuinely dead load, and both are network-aware; this
    // exists purely so a wedged tab cannot hang forever.
    absoluteMaxMs = 300000,
    // Quiet-network threshold for the starvation break: how long the network
    // must be silent, with a flat picture on screen, before we ask for a
    // finer tier. Short on purpose — every second here is avoidable wait.
    starveQuietMs = 2000
): Promise<void> {
    return new Promise((resolve) => {
        const device = app.graphicsDevice;
        const gl = (device as unknown as { gl?: WebGLRenderingContext }).gl;
        const start = performance.now();
        // Pixel source per backend. WebGL: gl.readPixels on the default
        // framebuffer inside 'postrender', synchronous (proven on iPhone
        // Safari + Chrome). WebGPU: the presented canvas cannot be read at all
        // (no readPixels, and drawImage() of it is black), so the probe reads
        // the CameraFrame's scene target instead — an async GPU copy that
        // lands a frame later, which is nothing for a probe sampling every
        // 10th frame. One read in flight at a time; a frame that arrives
        // while one is pending is simply skipped.
        let readInFlight = false;
        let finished = false;
        let frame = 0;
        let hits = 0;
        let probeErrors = 0;
        let starvedSteps = 0;
        let lastStarveAt = start;
        let deadNoteShown = false;
        let lastFrameActivityAt = start;
        // Last probe readings, reported with the reveal reason: when the gate
        // resolves by timeout instead of by content, THIS says which test the
        // picture on screen was failing (measured, not guessed).
        let lastLit = -1;
        let lastEdges = -1;
        let lastSpread = -1;
        let maxEdges = -1;
        // 3x3 grid of 64px rows: 576 samples spread over the frame.
        const PATCH = 64;
        const GRID = [0.3, 0.5, 0.7];
        const px = new Uint8Array(4 * PATCH);
        const lums = new Float32Array(PATCH * GRID.length * GRID.length);
        let prev: Float32Array | null = null;
        // Luminance values are on the r+g+b sum scale (0..765). Shared by both
        // backends; `nowMs` is the frame's clock for the sync path and the
        // read-completion time for the async one.
        const analyze = (nowMs: number) => {
                let lit = 0;
                let edges = 0;
                let minMean = Infinity;
                let maxMean = -Infinity;
                for (let p = 0; p < lums.length; p += PATCH) {
                    let sum = 0;
                    for (let i = 0; i < PATCH; i++) {
                        const l = lums[p + i];
                        sum += l;
                        if (l > 45) lit++;
                        // Contrast over an 8px stride, within the row. Adjacent
                        // 1px deltas were the old test and they FAIL on real
                        // splat content: at 2x DPR a smooth-walled interior
                        // (Comedor) never produced more than 1 such edge with
                        // the whole scene on screen, so the gate ran to the
                        // 45s stagnation timeout on every desktop load.
                        // Measured on the same frames: 8px-stride edges were
                        // 48-60 for content vs 0 for the wash.
                        if (i >= 8 && Math.abs(l - lums[p + i - 8]) > 60) edges++;
                    }
                    const mean = sum / PATCH;
                    if (mean < minMean) minMean = mean;
                    if (mean > maxMean) maxMean = mean;
                }
                // Brightness spread across the nine patches: a real scene is
                // lit unevenly (measured 280-450), the wash is one flat value
                // (measured 0 — and a soft gradient stays far below this).
                const spread = maxMean - minMean;
                let drift = Infinity;
                if (prev) {
                    drift = 0;
                    for (let i = 0; i < lums.length; i++) drift += Math.abs(lums[i] - prev[i]);
                    drift /= lums.length;
                } else {
                    prev = new Float32Array(lums.length);
                }
                prev.set(lums);
                lastLit = lit;
                lastEdges = edges;
                lastSpread = Math.round(spread);
                if (edges > maxEdges) maxEdges = edges;
                // A changing frame = decode/apply still working — that's
                // activity, keep waiting (resets the stagnation clock).
                if (drift >= 6) lastFrameActivityAt = performance.now();
                // Confirm on CONTENT, not on stillness: "has a real picture
                // appeared" is the only question that should hold the reveal.
                // Both tests must pass; each alone is 10x clear of the wash.
                if (lit >= 24 && edges >= 6 && spread >= 60) hits++;
                else hits = 0;
                if (hits >= 2) done('paint:pixels');
                // Fetch-starvation break (2 Sep): the boot clamp gates
                // DOWNLOADS as well as display — the engine only prefetches
                // tiers it is allowed to render. When the coarsest tier
                // renders as a flat field (i5lrl1xoaopz tier 3_0 on phones:
                // lit 576, edges 0, spread 0 on 4 of 11 phone loads) the
                // probe can never pass and the data that would fix it is
                // never requested; the stagnation timer then ended the wait
                // 45s later — ON the grey. Not a reveal exit (see below):
                // quiet network + flat picture = release one finer tier so
                // the engine FETCHES it, and let the pixel probe judge the
                // result like any other frame. Bounded by the tier count.
                // lastFrameActivityAt is in the quiet test too: a slow
                // decoder applies chunks with the network already idle, and
                // each apply moves pixels — that is progress, not starvation.
                // Step only when NOTHING is moving: no bytes, no requests, no
                // pixel change, and no step of ours, for starveQuietMs.
                if (hits === 0 && isNetIdle() &&
                    nowMs - Math.max(netActivityAt(), lastFrameActivityAt, lastStarveAt) > starveQuietMs) {
                    if (onStarved()) { starvedSteps++; lastStarveAt = nowMs; }
                }
                // There is deliberately NO second exit here. A "net idle AND
                // frame still" exit existed from 26 Aug (ac10af3): it assumed
                // the wash can only exist while bytes are pending, so a quiet
                // network meant the picture was final. The same day's
                // coarse-first boot clamp (afe4626) made that false — the
                // engine now fetches ONLY the coarsest tier before the reveal,
                // so the network goes quiet with the wash on screen, and that
                // exit revealed it on every phone load (telemetry: lit 576,
                // edges 0, spread 0 = uniform lit field = the grey screen).
                // The only thing that may release the loader is confirmed
                // content; a start view that never produces any is released
                // by the stagnation rule above, with the slow note showing.
        };
        const onPost = () => {
            frame++;
            const nowMs = performance.now();
            if (nowMs - start > absoluteMaxMs) { done('paint:absolute-cap'); return; }
            // Still working, just slowly — say so rather than sitting mute at 98%.
            if (nowMs - start > slowMs) onSlow();
            // Stagnant means BOTH clocks are cold: nothing is changing on screen
            // AND no scene bytes have landed. Either one alone means "still
            // working", so neither alone may end the wait.
            if (nowMs - Math.max(lastFrameActivityAt, netActivityAt()) > stagnantMs) {
                // Stagnation used to reveal unconditionally — the grey
                // screen's last open door (2 Sep): dead network + flat coarse
                // tier = grey reveal at 45s. If the frame has NEVER shown
                // structure there is nothing worth revealing: keep the loader
                // up, say the connection is gone, and keep probing so a
                // recovered connection resumes the normal path. Only the
                // absolute cap ends a truly dead wait now. If the picture did
                // show structure at some point, revealing beats sitting.
                if (maxEdges >= 6) { done('paint:stagnant'); return; }
                if (!deadNoteShown) { deadNoteShown = true; onDead(); }
                return;
            }
            if (frame % 10 !== 0) return;
            if (gl) {
                try {
                    const w = gl.drawingBufferWidth;
                    const h = gl.drawingBufferHeight;
                    let k = 0;
                    for (const fy of GRID) {
                        for (const fx of GRID) {
                            gl.readPixels((w * fx - PATCH / 2) | 0, (h * fy) | 0, PATCH, 1, gl.RGBA, gl.UNSIGNED_BYTE, px);
                            for (let i = 0; i < PATCH; i++) {
                                lums[k++] = px[i * 4] + px[i * 4 + 1] + px[i * 4 + 2];
                            }
                        }
                    }
                    analyze(nowMs);
                } catch {
                    // readPixels refused. Counted rather than ignored: if this is
                    // what happens on iOS, the pixel gate has never actually run
                    // there and every reveal was the settle gate's call alone.
                    probeErrors++;
                }
                return;
            }
            if (readInFlight) return;
            // Rows measured on the scene TARGET, not the device: behind the
            // stand demo's loader the target is a quarter of the canvas
            // (renderLight), ~100 px wide on a phone in portrait, and a 64 px
            // row at 70% across ran past its edge. On WebGPU that copy
            // invalidated the whole frame — black scene, probe never fed,
            // loader never released (iPhone 17 Pro, 14/10/2026). Clamp each
            // row inside the target; readSceneRect refuses anything else.
            const st = sceneTargetOf(app);
            if (!st) return;
            const w = st.texture.width;
            const h = st.texture.height;
            if (w < PATCH || h < 1) return;
            readInFlight = true;
            const reads: Promise<Uint8ClampedArray>[] = [];
            for (const fy of GRID) {
                for (const fx of GRID) {
                    const rx = Math.max(0, Math.min(w - PATCH, (w * fx - PATCH / 2) | 0));
                    const ry = Math.max(0, Math.min(h - 1, (h * fy) | 0));
                    reads.push(readSceneRect(app, rx, ry, PATCH, 1));
                }
            }
            Promise.all(reads).then((rows) => {
                readInFlight = false;
                if (finished) return;
                let k = 0;
                for (const row of rows) {
                    for (let i = 0; i < PATCH; i++) {
                        lums[k++] = row[i * 4] + row[i * 4 + 1] + row[i * 4 + 2];
                    }
                }
                analyze(performance.now());
            }).catch(() => {
                readInFlight = false;
                probeErrors++;
            });
        };
        const done = (reason: string) => {
            finished = true;
            app.off('postrender', onPost);
            (window as unknown as Record<string, unknown>).__revealReason = {
                paint: reason,
                starved: starvedSteps,
                probeErrors,
                frames: frame,
                ms: Math.round(performance.now() - start),
                lit: lastLit,
                edges: lastEdges,
                spread: lastSpread,
                maxEdges
            };
            resolve();
        };
        app.on('postrender', onPost);
    });
}

/**
 * Resolve once the streamed splat has SETTLED for the current view — the
 * "grey mush sharpening" phase that used to play AFTER the loader revealed
 * now finishes behind it (and the progress bar keeps counting its bytes).
 *
 * Signal: the unified GSplat pipeline's per-frame 'frame:ready' event, which
 * reports whether the sorter has caught up with the world state (`ready`)
 * and how many chunk files are still pending (`loadingCount`). We wait for
 * STABILITY, not for loadingCount===0: the engine legitimately parks files
 * as pending forever (budget-deferred uploads, speculative prefetch), so
 * zero may never come. "ready, and the pending count hasn't changed for
 * `stableMs`" = nothing is improving the visible frame any more. Any chunk
 * landing (count drops), new request (count rises) or re-sort (ready flips)
 * restarts the window. The check rides the event itself — frame-paced, so
 * it can't be starved by the very load it is measuring.
 *
 * What "settled" means here, learned the hard way (throttled-network runs):
 * while chunk files stream in, the engine can render a screen-filling grey
 * WASH (splats whose position texture arrived but whose scale/colour data
 * hasn't) — and during that whole phase it happily reports ready=true with a
 * frozen pendingLoadCount; the batch only APPLIES once downloads finish. So:
 *   - Stability requires the byte tracker to report zero in-flight scene
 *     requests (the one signal that stayed truthful throughout), AND ready,
 *     AND an unchanged pending count — for a wall-time floor and a minimum
 *     number of frame:ready events (frames are the clock that can't be
 *     starved by load jank; wall-clock alone lies at 5 fps).
 *   - The escape hatch is a WEDGE detector, not a wall-clock cap: force the
 *     reveal only when nothing has progressed at all (no bytes, no counter
 *     or sort transitions) for `wedgeMs`. A slow-but-healthy load can take
 *     as long as it needs — the bar is visibly moving through all of it —
 *     while a truly dead stream still can't hold the reveal hostage.
 *   - Plain .ply scenes never fire 'frame:ready' (non-unified): counting 8
 *     RENDERED frames without an event hands control back to the paint
 *     probe alone.
 * An absolute backstop cap remains for belt-and-braces.
 */
/**
 * Resolve once the COARSE preload is in: the coarsest tier the octree fetched
 * plus `extraTiers` finer ones, each fully settled (seen, nothing in flight).
 *
 * Why this instead of waiting for the stream to go quiet: the tiers are four
 * INDEPENDENT copies of the scene at 100/50/25/12.5% of the gaussians, not an
 * incremental encoding, so waiting for the finest tier means waiting for the
 * biggest single download of the four. The two coarsest together are about a
 * fifth of the bundle. Revealing there — with those tiers already resident —
 * is what lets the coarse→fine animation run off the GPU instead of off the
 * network, so it cannot stall halfway through.
 *
 * `dwellMs` guards a transient idle: the climb is per-node and staged, so
 * in-flight can touch zero for a frame between batches.
 */
function waitForCoarsePreload(
    getTiers: () => Map<number, { done: number; inflight: number }>,
    extraTiers = 1,
    dwellMs = 400,
    // Single-tier scenes and plain .ply never produce a second tier; don't
    // hold the reveal hostage waiting for one that is never coming.
    giveUpMs = 20000
): Promise<{ coarsest: number | null }> {
    return new Promise((resolve) => {
        const start = performance.now();
        let okSince = 0;
        const id = window.setInterval(() => {
            const tiers = getTiers();
            const seen = [...tiers.keys()];
            const coarsest = seen.length ? Math.max(...seen) : null;
            const settled = (t: number) => {
                const e = tiers.get(t);
                return !!e && e.done > 0 && e.inflight === 0;
            };
            let ready = false;
            if (coarsest !== null) {
                ready = true;
                for (let i = 0; i <= extraTiers; i++) {
                    const t = coarsest - i;
                    if (t < 0) break;              // no finer tier exists
                    if (!tiers.has(t)) { ready = i > 0; break; } // not requested yet
                    if (!settled(t)) { ready = false; break; }
                }
            }
            const now = performance.now();
            if (ready) {
                if (!okSince) okSince = now;
                if (now - okSince >= dwellMs) { window.clearInterval(id); resolve({ coarsest }); }
            } else {
                okSince = 0;
            }
            if (now - start > giveUpMs) { window.clearInterval(id); resolve({ coarsest }); }
        }, 100);
    });
}

/**
 * The reveal flourish. Pins the display to the coarsest tier, then walks the
 * clamp down to full detail over `totalMs`. Every step lands on data that is
 * ALREADY resident, so each is instant — the scene visibly comes to life on a
 * schedule we control rather than one the network dictates.
 *
 * Caveat worth knowing: while the clamp is on, the engine will not prefetch
 * finer tiers either (raising the floor raises what it considers optimal), so
 * this delays the finest tier by roughly `totalMs`. That is the price of the
 * effect, and it is bounded.
 */
function playLodReveal(app: pc.AppBase, coarsest: number, totalMs = 2400): Array<() => void> {
    if (coarsest <= 0) return [];
    const steps = coarsest;               // coarsest -> ... -> 0
    const every = Math.max(1, Math.round(totalMs / steps));
    const timers: number[] = [];
    setLodFloor(app, coarsest);           // first visible frame is the blobs
    for (let i = 1; i <= steps; i++) {
        timers.push(window.setTimeout(() => { setLodFloor(app, coarsest - i); }, every * i));
    }
    return [() => {
        timers.forEach(window.clearTimeout);
        setLodFloor(app, 0);              // never leave the scene clamped
    }];
}

function waitForSplatSettled(
    app: pc.AppBase,
    isNetIdle: () => boolean,
    lastNetActivityAt: () => number,
    stableMs = 650,
    wedgeMs = 6000,
    // See waitForSplatPainted: a wall-clock ceiling must not be able to
    // out-race a healthy download. The wedge detector is the real escape.
    absoluteMaxMs = 180000
): Promise<void> {
    return new Promise((resolve) => {
        const sys = (app.systems as unknown as { gsplat?: pc.EventHandler }).gsplat;
        if (!sys || typeof sys.on !== 'function') { resolve(); return; }
        let settled = false;
        let sawEvent = false;
        let lastLoading = -1;
        let lastTransitionAt = performance.now();
        let stableSince = 0;
        let stableEvents = 0;
        let framesWithoutEvent = 0;
        const settleStart = performance.now();
        const done = (reason: string) => {
            if (settled) return; // first caller owns the reason
            settled = true;
            const w = window as unknown as Record<string, unknown>;
            const prev = (w.__revealReason ?? {}) as Record<string, unknown>;
            w.__revealReason = {
                ...prev,
                settle: reason,
                settleMs: Math.round(performance.now() - settleStart)
            };
            window.clearTimeout(capTimer);
            sys.off('frame:ready', onReady);
            app.off('postrender', onPost);
            resolve();
        };
        const capTimer = window.setTimeout(() => done('settle:absolute-cap'), absoluteMaxMs);
        const onPost = () => {
            if (!sawEvent && ++framesWithoutEvent >= 8) { done('settle:no-event-nonunified'); return; }
            // Wedge check, frame-paced: no bytes AND no state transitions.
            const now = performance.now();
            if (now - Math.max(lastTransitionAt, lastNetActivityAt()) > wedgeMs) done('settle:wedge');
        };
        // Load-phase trace for field debugging (dump window.__loadTrace).
        const trace = ((window as unknown as Record<string, unknown>).__loadTrace ??= []) as unknown[];
        const onReady = (_cam: unknown, _layer: unknown, ready: boolean, loadingCount: number) => {
            sawEvent = true;
            const now = performance.now();
            if (trace.length < 2000) trace.push([Math.round(now), ready ? 1 : 0, loadingCount, isNetIdle() ? 1 : 0]);
            if (!ready || loadingCount !== lastLoading || !isNetIdle()) {
                if (!ready || loadingCount !== lastLoading) lastTransitionAt = now;
                lastLoading = loadingCount;
                stableSince = now;
                stableEvents = 0;
                return;
            }
            stableEvents++;
            if (stableEvents >= 6 && now - stableSince >= stableMs) done('settle:stable');
        };
        sys.on('frame:ready', onReady);
        app.on('postrender', onPost);
    });
}

/** Scene files in flight at once in the stand demo (see mount()). */
const DEMO_INFLIGHT = 4;
/** Stand demo: full-size frames drawn behind the loader before its fade
 *  may start, and the most it waits for them (ms). */
const RESTORE_FRAMES = 3;
const RESTORE_CAP_MS = 1500;

/** Stand demo: the longest the loader's black may wait after the reveal for
 *  the scene to settle before it opens anyway (see mount()). */
const DEMO_SETTLE_CAP_MS = 4000;

let teardownFilterInstalled = false;
function installTeardownNoiseFilter(): void {
    if (teardownFilterInstalled) return;
    teardownFilterInstalled = true;
    window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
        const msg = String((e.reason as { message?: string } | undefined)?.message ?? e.reason ?? '');
        if (/Texture read did not complete, as the graphics device was destroyed/.test(msg)) e.preventDefault();
    });
}

export async function mount(opts: MountOptions): Promise<ViewerHandle> {
    const { container, sceneUrl } = opts;
    const embed = opts.embed ?? false;
    const demo = opts.demo ?? false;
    const ui = {
        loadingScreen: opts.ui?.loadingScreen ?? true,
        toolbar:       opts.ui?.toolbar       ?? true,
        info:          opts.ui?.info          ?? true,
        branding:      opts.ui?.branding      ?? true,
        fps:           opts.ui?.fps           ?? false,   // dev only
        quality:       opts.ui?.quality       ?? false,
        hints:         opts.ui?.hints         ?? true
    };

    // ── Build internal layout ─────────────────────────────────────────────
    // Mount mode might be a small div on a host page, so don't assume the
    // container has the standalone-page DOM. Build our own.
    container.classList.add('viewer-mount');
    const canvas = document.createElement('canvas');
    canvas.className = 'viewer-canvas';
    // Also give the first mounted viewer the standalone-page id so devtools
    // / debug scripts that look up `#viewer-canvas` keep working. Subsequent
    // mounts on the same page would collide, so guard against re-adding.
    if (!document.getElementById('viewer-canvas')) canvas.id = 'viewer-canvas';
    container.appendChild(canvas);

    const uiRoot = document.createElement('div');
    // Use the legacy viewer's class name so the .ollo-* chrome CSS scopes apply.
    uiRoot.className = 'ollo-ui';
    if (!document.getElementById('viewer-ui')) uiRoot.id = 'viewer-ui';
    container.appendChild(uiRoot);

    // iPadOS long-press. viewer.css opts the whole viewer out of selection,
    // yet a finger held on the canvas (hold-to-walk) still raised the blue
    // selection, grabbing text the finger never touched ("galeria") with a
    // Copy / Find Selection menu. Cancelling the touch's default at the source
    // stops the gesture outright. The controllers run on pointer events, which
    // a cancelled touchstart leaves alone; the canvas's only click listeners
    // are desktop pointer-lock. Canvas only — a cancelled touchstart on a
    // button would swallow its click. (The fixed joystick pad does the same.)
    const onCanvasTouchStart = (e: TouchEvent) => {
        e.preventDefault();
        // A cancelled touch no longer moves focus: drop the keyboard of a
        // field in our UI by hand, as a tap on the view always did.
        const active = document.activeElement;
        if (active instanceof HTMLElement && active !== document.body && container.contains(active)) active.blur();
    };
    // Backstop for anything that still starts a selection inside the viewer.
    // The places that WANT one (form fields, the save-image rung, the
    // diagnostics log) keep it — same list as viewer.css.
    const onSelectStart = (e: Event) => {
        const node = e.target as Node | null;
        const el = node && (node.nodeType === Node.ELEMENT_NODE ? node as Element : node.parentElement);
        if (el?.closest('input, textarea, .ollo-save-img, .ollo-diag-body')) return;
        e.preventDefault();
    };
    canvas.addEventListener('touchstart', onCanvasTouchStart, { passive: false });
    container.addEventListener('selectstart', onSelectStart);

    let scene: LoadedScene | null = null;
    let quality: AdaptiveQualityController | null = null;
    /** Has the splat actually painted? Gates the toolbar's capture button — a
     *  capture before this is a valid but empty #070707 frame. */
    let painted = false;
    const cleanupCallbacks: Array<() => void> = [];
    cleanupCallbacks.push(() => {
        canvas.removeEventListener('touchstart', onCanvasTouchStart);
        container.removeEventListener('selectstart', onSelectStart);
    });

    // ── Boot PlayCanvas ───────────────────────────────────────────────────
    const app = await ViewerApp.create(canvas);
    app.start();

    // Stand demo: how many scene files the engine may have in flight. The
    // files come from the device's own cache, so with the engine's default
    // (128) everything lands in one burst and the GPU uploads that follow
    // stall the whole screen — the loader's video and bar with it, on every
    // platform (Lenny, 13/10/2026: "it still lags, every single time").
    // A handful at a time trickles the uploads across frames: a slower
    // load, a smooth one — the trade he asked for. `?inflight=N` to tune.
    // The limit lives on the engine's one HTTP queue, so a plain visitor
    // page gets the default back.
    {
        let inflight = demo ? DEMO_INFLIGHT : 128;
        try {
            const q = Number(new URL(window.location.href).searchParams.get('inflight'));
            if (demo && q > 0) inflight = q;
        } catch { /* no URL — keep the default */ }
        (app.app.loader as unknown as { maxConcurrentRequests: number }).maxConcurrentRequests = inflight;
    }

    // ViewerApp.create installs FILLMODE_FILL_WINDOW by default (for the
    // standalone fullscreen page). For embed mounts we want the canvas to
    // track the container's size, not the viewport.
    app.app.setCanvasFillMode(FILLMODE_NONE);
    app.app.setCanvasResolution(RESOLUTION_AUTO);
    const fitCanvas = () => {
        const w = Math.max(1, container.clientWidth);
        const h = Math.max(1, container.clientHeight);
        canvas.style.width = `${w}px`;
        canvas.style.height = `${h}px`;
        app.app.resizeCanvas();
    };
    const resizeObs = new ResizeObserver(() => fitCanvas());
    resizeObs.observe(container);
    fitCanvas();
    cleanupCallbacks.push(() => resizeObs.disconnect());

    let loadingScreen: LoadingScreen | null = null;
    if (ui.loadingScreen) {
        loadingScreen = new LoadingScreen(uiRoot, {
            block3d: demo,
            // Stand demo: the loader's plan is cut to this scene's last
            // measured load on this device (recorded at reveal, below), never
            // under its minimum life; a first open assumes a typical one.
            expectedMs: demo ? (expectedLoadMs(opts.sceneUrl) ?? 4000) : null,
            video: demo ? opts.loaderVideo ?? null : null,
            onMetrics: demo ? (m) => { loaderMetrics = { dropped: m.droppedFrames, total: m.totalFrames }; } : undefined,
            // Demo: the joystick pad is a sibling of the ui root and paints
            // above the overlay (viewer.css hides .ollo-joystick under this
            // class) — it shows from the moment the overlay fades.
            onHide: demo ? () => container.classList.remove('ollo-loading-up') : undefined
        });
        if (demo) container.classList.add('ollo-loading-up');
        cleanupCallbacks.push(() => { loadingScreen?.dispose(); container.classList.remove('ollo-loading-up'); });
    } else {
        // Host asked for no loading UI — make sure the standalone page's
        // static boot loader doesn't sit there forever.
        document.getElementById('ollo-boot-loading')?.remove();
    }

    // Load diagnostics: ALWAYS collecting, never visible until asked for.
    // `?t0=<epoch ms>` lets the host page hand us the moment the user actually
    // clicked, so CLICK→SCENE includes the parent's click→iframe hop instead of
    // starting from this document's own navigation. Without it we fall back to
    // navigation start and say so in the report.
    let diagOrigin = performance.timeOrigin;
    let clickWasReal = false;
    let wantsDebug = false;
    try {
        const q = new URLSearchParams(window.location.search);
        wantsDebug = q.get('debug') === '1';
        const t0 = Number(q.get('t0'));
        // Sanity-bound it: a bogus or stale t0 would poison every number.
        if (Number.isFinite(t0) && t0 > 0 && Date.now() - t0 < 120_000) {
            diagOrigin = t0;
            clickWasReal = true;
        }
    } catch { /* defaults are fine */ }
    const diag = new LoadDiagnostics(uiRoot, opts.sceneUrl, diagOrigin, clickWasReal, !demo);
    cleanupCallbacks.push(() => diag.dispose());
    if (wantsDebug) diag.toggle();

    // ── Real download progress, paced over the WHOLE load ─────────────────
    // One byte-ratio over every request the engine drives for this scene.
    // The LOD streamer discovers chunks as it goes, so the denominator GROWS
    // mid-load and the raw ratio dips. The old code clamped those dips
    // monotonic, which turned every discovery into a dead stall. Instead,
    // RE-ANCHOR: freeze the bar where it is and map the remaining raw range
    // onto the remaining track.
    //
    // Pacing: bytes alone made the bar sprint to ~95% and then PARK for the
    // decode/apply tail — which on phones costs about as long as the
    // download itself. So the run is choreographed over BOTH phases:
    //   - the byte band's ceiling adapts to the phases' relative durations
    //     (measured download time so far vs a decode-time estimate from
    //     bytes landed at a nominal decode rate). Slow networks push the
    //     ceiling up toward ~0.93, fast ones leave room for the tail;
    //   - once the stream goes quiet, the tail eases from wherever the bar
    //     is toward 0.985 across that decode estimate, so the last stretch
    //     runs at a believable steady pace instead of parking. Estimates
    //     are deliberately a touch generous: early completion just triggers
    //     complete()'s sprint, late completion slows the crawl.
    // Deliberate pacing, honest endpoints: 100% still belongs exclusively
    // to the readiness gate below.
    //
    // Two pipeline-stage caps hold the bar back while work we KNOW is coming
    // has no requests yet: scene.json alone can't take it past 6%, and an
    // octree's lod-meta can't take it past 30% until chunks show up.
    const DECODE_BYTES_PER_MS = 3500;   // ≈3.5 MB/s nominal decode+apply rate
    const TAIL_END = 0.985;
    const sceneAbs = new URL(sceneUrl, window.location.href);
    const sceneBase = sceneAbs.href.slice(0, sceneAbs.href.lastIndexOf('/') + 1);
    const isSceneUrl = (u: string): boolean => {
        try {
            const abs = new URL(u, window.location.href).href;
            return abs.startsWith(sceneBase) || abs.includes('/editor-scenes/');
        } catch { return false; }
    };
    let fed = 0;        // last value actually fed to the bar (post-cap)
    let anchorFed = 0;  // bar position when the denominator last grew
    let anchorRaw = 0;  // raw ratio at that moment
    let lastRaw = 0;
    let inflightNow = 0; // scene requests in flight — the settle gate reads this
    let tiersNow = new Map<number, { done: number; inflight: number }>();
    let lastLoadedBytes = 0;
    // ?debug=1 load diagnostics. netBusyMs accumulates ONLY while a scene
    // request is in flight, which is what separates "the network is slow" from
    // "we are not asking for anything" — see ui/load-stats.ts.
    const bootAt = performance.now();
    // Demo: how badly the main thread stalls during this load, stored with
    // the load time (src/demo/load-clock.ts) and shown in Setup.
    const stallMeter = startStallMeter();
    cleanupCallbacks.push(() => { stallMeter.stop(); });
    let loaderMetrics: { dropped: number; total: number } | undefined;
    // Demo: writes the load record again once the scene reaches full detail.
    let recordOnSettle: (() => void) | null = null;
    let netBusyMs = 0;
    let busySampleAt = 0;
    const marks: { sceneJsonMs: number | null; lodMetaMs: number | null;
        firstChunkMs: number | null; revealMs: number | null; settledMs: number | null } =
        { sceneJsonMs: null, lodMetaMs: null, firstChunkMs: null, revealMs: null, settledMs: null };
    let lastNetActivityAt = performance.now();
    let firstByteAt = 0;
    let sawChunk = false;
    const decodeEtaMs = () =>
        Math.min(10000, Math.max(1500, lastLoadedBytes / DECODE_BYTES_PER_MS));
    const netBandEnd = () => {
        if (!firstByteAt) return 0.15;
        const elapsed = performance.now() - firstByteAt;
        return 0.15 + 0.78 * (elapsed / (elapsed + decodeEtaMs()));
    };
    const stopTracking = trackSceneDownloads(isSceneUrl, (s) => {
        if (s.loadedBytes !== lastLoadedBytes || s.inflight !== inflightNow) {
            lastNetActivityAt = performance.now();
        }
        if (s.loadedBytes > 0 && !firstByteAt) firstByteAt = performance.now();
        // Busy-time integration: charge the elapsed slice to the network only
        // when it actually had work outstanding.
        const nowMs = performance.now();
        if (busySampleAt && inflightNow > 0) netBusyMs += nowMs - busySampleAt;
        busySampleAt = nowMs;
        if (marks.sceneJsonMs === null && !s.onlySceneJson) marks.sceneJsonMs = nowMs - bootAt;
        if (marks.lodMetaMs === null && s.lodMetaSeen) marks.lodMetaMs = nowMs - bootAt;
        if (marks.firstChunkMs === null && [...s.tiers.values()].some(v => v.done > 0)) {
            marks.firstChunkMs = nowMs - bootAt;
        }
        diag.onProgress({ loadedBytes: s.loadedBytes, inflight: s.inflight, tiers: s.tiers });
        if (s.lodMetaSeen) diag.mark('lod-meta');
        lastLoadedBytes = s.loadedBytes;
        inflightNow = s.inflight;
        tiersNow = s.tiers;
        sawChunk = sawChunk || s.chunkSeen;
        if (s.raw < lastRaw) {
            anchorFed = fed;
            anchorRaw = s.raw;
        }
        lastRaw = s.raw;
        const span = 1 - anchorRaw;
        const t = span > 1e-4 ? (s.raw - anchorRaw) / span : 1;
        const cap = s.onlySceneJson ? 0.06 :
            (s.lodMetaSeen && !s.chunkSeen ? 0.30 : 1);
        const d = Math.min(cap, anchorFed + t * Math.max(0, netBandEnd() - anchorFed));
        if (d > fed) {
            fed = d;
            loadingScreen?.setProgress(d);
        }
    });
    cleanupCallbacks.push(stopTracking);


    // Decode-tail pacing: rides the app's update loop (frame-paced — timers
    // starve exactly when decode is grinding). Arms when the stream first
    // goes quiet after chunks were seen; re-arms if the stream wakes again
    // (LOD refinement wave) so bytes take back over in between.
    let tailStartAt = 0;
    let tailFrom = 0;
    let tailEta = 1;
    const onPaceTick = () => {
        if (!loadingScreen) return;
        const now = performance.now();
        if (inflightNow > 0) { tailStartAt = 0; return; }
        if (!tailStartAt) {
            if (sawChunk && now - lastNetActivityAt > 350) {
                tailStartAt = now;
                tailFrom = fed;
                tailEta = decodeEtaMs();
            }
            return;
        }
        const p = Math.min(1, (now - tailStartAt) / tailEta);
        const eased = 1 - (1 - p) * (1 - p); // easeOutQuad — fast start, gentle finish
        let d = tailFrom + (TAIL_END - tailFrom) * eased;
        if (p >= 1) {
            // Decode outlived the estimate (slow GPUs — iPhone Chrome):
            // keep a barely-perceptible asymptotic crawl toward 0.995
            // instead of a dead park at the ceiling.
            const over = now - tailStartAt - tailEta;
            d = TAIL_END + (0.995 - TAIL_END) * (1 - Math.exp(-over / 6000));
        }
        if (d > fed) {
            fed = d;
            loadingScreen.setProgress(d);
        }
    };
    app.app.on('update', onPaceTick);
    cleanupCallbacks.push(() => app.app.off('update', onPaceTick));
    const fpsOverlay = new FpsOverlay(app.app, uiRoot);
    fpsOverlay.setVisible(ui.fps);
    // Make the canvas visible on the first render so the splat draws BEHIND the
    // still-opaque animated loader. We do NOT fade the loader here — revealing on
    // the first frame showed a black screen while the splat was still
    // downloading/decoding. The loader fades only once the splat has actually
    // materialised (after the await below).
    app.onFirstFrame(() => {
        canvas.classList.add('visible');
    });

    // Stand demo: render LIGHT while the loader is up. Nobody sees the scene
    // yet, and a full-resolution sort, projection and raster over millions
    // of splats sixty times a second is most of the GPU work that starves
    // the compositor — the loader's video and bar with it (loader-only test
    // smooth, real load jerky; Lenny, 13/10/2026). Until the scene is
    // settled: a quarter of the pixels and twenty frames a second (the
    // engine's update still runs every frame; only the render is paced, so
    // streaming and the settle gate are unaffected). Restored before the
    // loader's fade, so the first full frames are drawn behind it.
    // Restoring has to RESIZE the canvas: maxPixelRatio only takes effect on
    // the next resizeCanvas(), and without one the scene stayed at a quarter
    // of the pixels after the fade until the quality controller's own resize
    // ~5 s later — on the iPad a strip at the bottom first, then the rest
    // (Lenny, 15/10/2026). The returned promise resolves once a few frames
    // at full size have been drawn, so the fade opens on the finished frame.
    let renderLightTimer = 0;
    let restoreRender: (() => Promise<void>) | null = null;
    // Demo: resolves when the loader has closed on the settled scene. The
    // flythrough recorder starts from there.
    let demoReady: Promise<void> | null = null;
    if (demo) {
        const dev = app.app.graphicsDevice as unknown as { maxPixelRatio: number };
        let fullRatio = dev.maxPixelRatio;
        const light = Math.min(fullRatio, 0.25);
        app.app.autoRender = false;
        // Re-applied each tick: the quality controller's constructor sets
        // the device profile's ratio when the scene has loaded — that ratio
        // is the one to restore.
        renderLightTimer = window.setInterval(() => {
            if (dev.maxPixelRatio !== light) fullRatio = dev.maxPixelRatio;
            dev.maxPixelRatio = light;
            app.app.resizeCanvas();
            app.app.renderNextFrame = true;
        }, 50);
        restoreRender = () => {
            restoreRender = null;
            window.clearInterval(renderLightTimer);
            if (dev.maxPixelRatio !== light) fullRatio = dev.maxPixelRatio;
            dev.maxPixelRatio = fullRatio;
            app.app.resizeCanvas();
            app.app.autoRender = true;
            app.app.renderNextFrame = true;
            return new Promise<void>((resolve) => {
                let frames = 0;
                const done = () => { app.app.off('frameend', onFrame); window.clearTimeout(cap); resolve(); };
                const onFrame = () => { if (++frames >= RESTORE_FRAMES) done(); };
                // A starved device still gets its fade.
                const cap = window.setTimeout(done, RESTORE_CAP_MS);
                app.app.on('frameend', onFrame);
            });
        };
        cleanupCallbacks.push(() => { void restoreRender?.(); });
    }

    try {
        // ── Coarse-first boot clamp ──────────────────────────────────────
        // Pin every octree splat to its COARSEST tier while loading. Without
        // this the engine requests all tiers of every visible node up front,
        // and its batch apply means nothing paints until the whole bundle is
        // down — on a slow pipe that is minutes of black screen, and the LOD
        // cascade never earns its keep. Clamped, the first apply (and the
        // pixel probe's reveal) needs only the smallest tier. The engine
        // clamps this value to each asset's real max LOD, so an absurdly high
        // number safely means "coarsest available" for any tier count.
        // Released by playLodReveal after the reveal, which is what lets the
        // finer tiers stream BEHIND the visible scene instead of in front of
        // a black screen.
        // Per-component since engine 2.20 (scene/lod-clamp.ts fans it out,
        // including to splats the loader creates after this line).
        setLodFloor(app.app, 99);

        scene = await loadScene(sceneUrl, {
            app: app.app,
            cameraEntity: app.cameraEntity
        });

        // ── Everything that defines the LOOK of the first visible frame goes
        // BEFORE the readiness gate, so the reveal lands on the finished
        // scene instead of restyling it in front of the user (that restyling
        // was the "grey second loading screen" after the bar completed).

        // Lighting: tonemap + exposure are native to the camera; bloom, contrast and
        // colour enhance need the CameraFrame compose, so such scenes leave the
        // 16-bit direct pipeline here, before the first reveal.
        if (app.editorPipeline && lightingNeedsCompose(scene.file.lighting)) {
            app.useCameraFrame();
        }
        if (app.editorPipeline) {
            applyLightingNative(app.app, app.cameraEntity, scene.file.lighting);
        } else {
            applyLighting(app.cameraFrame, scene.file.lighting);
        }

        // Camera clip planes for whatever nav follows — also affects what the
        // first visible frame culls, so it belongs before the gate.
        const cam = app.cameraEntity.camera;
        if (cam) {
            cam.farClip = Math.max(cam.farClip, scene.sceneRadius * 8);
            cam.nearClip = Math.max(0.1, scene.sceneRadius * 0.001);
        }

        // Adaptive quality: the CONSTRUCTOR applies the device-tier profile
        // (render-target scale, splat budget, lodMul). On phones that halves
        // lodBaseDistance — a different LOD working set. Doing it here means
        // the readiness gate below waits for the FINAL chunk set at the FINAL
        // resolution; previously this ran after the reveal and re-streamed
        // the scene in front of the user. The FPS-adaptation monitor itself
        // (start()) only begins after the reveal, so its warmup measures the
        // steady state, not the load.
        const profile = detectDevice(canvas);
        quality = new AdaptiveQualityController(
            app.app,
            [...scene.splats.values()],
            profile,
            // Stand demo: a device we know, so start at editor parity and only
            // step down on a real stutter — not up from the phone rung.
            demo ? { startLevel: 0 } : {}
        );
        // A quality this browser picked in Settings before: applied here, so
        // the readiness gate below already waits for that working set.
        const picked = savedQuality();
        if (picked) quality.setMode(picked);

        // Ready = painted (pixel probe: real content with contrast on screen,
        // not the half-applied grey wash) AND settled (stream/decode quiet).
        // Run CONCURRENTLY: the settle side often stabilises while the probe
        // is still waiting out the wash, so the reveal fires the moment
        // content pops. The tracker keeps feeding the bar through both — the
        // refinement bytes are part of "loading" now, not an after-show.
        // complete() then reveals in under half a second; no timers, no
        // guesswork tail.
        // Reveal on COARSE PRELOAD, not on the stream going quiet. Waiting for
        // quiet meant waiting for the finest tier — the largest of the four
        // independent copies — while a perfectly coherent picture had been
        // renderable for ages. The pixel probe still has the veto, so this can
        // only ever reveal real content, never the half-applied wash.
        // FASTEST HONEST REVEAL: the pixel probe, alone. Anything else added to
        // this AND is pure delay — waiting for the stream to go quiet cost
        // minutes, and waiting for whole tiers to finish cost most of that
        // again (measured: at 0.4 Mbps the coarsest tier still had 6 chunks in
        // flight at 150s). The probe already answers the only question that
        // protects the user: is there real, non-wash content on screen.
        // Release one finer LOD tier: the starvation break's lever. 99 means
        // "coarsest available", so the first step lands on maxSeen - 1.
        const stepClampDown = (): boolean => {
            const maxSeen = tiersNow.size ? Math.max(...tiersNow.keys()) : null;
            if (maxSeen === null) return false; // nothing streamed yet — nothing to release
            const effective = Math.min(getLodFloor(app.app), maxSeen);
            if (effective <= 0) return false;   // full detail already allowed
            setLodFloor(app.app, effective - 1);
            return true;
        };
        await waitForSplatPainted(
            app.app,
            () => lastNetActivityAt,
            () => loadingScreen?.showSlowNote(),
            () => inflightNow === 0,
            stepClampDown,
            () => loadingScreen?.showStalledNote()
        );
        marks.revealMs = performance.now() - bootAt;
        if (demo) {
            // Stand demo: the stall meter runs on through the LOD walk after
            // the reveal (the finer tiers applying behind the scene — the
            // "freeze at the end"), then the load and its stalls are stored
            // for Setup and for the loader's clock. A settled-first reveal
            // was tried on 12/10/2026 (every tier applied behind the loader,
            // then a fade): slower and no smoother, since the stalls moved
            // under the loader instead of going away. Reverted 13/10/2026.
            const revealMs = marks.revealMs;
            // The loader lives on past the reveal for its minimum life, and
            // its dropped-frame count arrives when it goes: record at the
            // later of the two.
            // The full-detail gate's verdict goes in too (reason + time, or
            // 'pending'): Setup shows it, which is how a device where the
            // black after the animation runs long can say why (13/10/2026).
            const record = () => {
                const rr = (window as unknown as { __revealReason?: { settle?: string } }).__revealReason;
                recordLoad(opts.sceneUrl, {
                    ms: revealMs, ...stallMeter.stop(),
                    markMs: { reveal: Math.round(revealMs), settle: marks.settledMs === null ? undefined : Math.round(marks.settledMs) },
                    settle: marks.settledMs === null ? 'pending' : (rr?.settle ?? 'settle:?').replace(/^settle:/, ''),
                    video: loaderMetrics
                });
            };
            window.setTimeout(record, 3000);
            window.setTimeout(record, 5000);
            recordOnSettle = record;
        }
        if (app.bypassCameraFrame) {
            // ?cf=0: the probe is done with the CameraFrame; render like SuperSplat from here on
            app.cameraFrame.enabled = false;
        }
        {
            // Which tiers were resident at reveal — the number that says
            // whether the starvation break fired for the right reason.
            const w = window as unknown as { __revealReason?: Record<string, unknown> };
            const prev = w.__revealReason ?? {};
            w.__revealReason = { ...prev, tiers: [...tiersNow.keys()].sort((a, b) => a - b).join('+') };
        }
        diag.setScene(scene.file.name); // before mark(), which writes the history row
        diag.mark('REVEAL');
        // Flourish, opportunistic: clamp to the coarsest tier we have actually
        // seen and walk up. If finer tiers are not resident yet the engine just
        // holds each node at its best available tier, so this degrades to a
        // plain reveal rather than misbehaving. Walking the clamp down is ALSO
        // what releases the boot clamp above — each step lets the engine start
        // streaming the next finer tier behind the already-visible scene.
        const coarsestSeen = tiersNow.size ? Math.max(...tiersNow.keys()) : null;
        const revealFrom = coarsestSeen === null
            ? null
            : Math.min(coarsestSeen, getLodFloor(app.app));
        if (revealFrom !== null && revealFrom > 0) {
            // Demo: a quick walk, so every tier is applied — and the GPU
            // quiet — well inside the loader's minimum life (see below).
            cleanupCallbacks.push(...playLodReveal(app.app, revealFrom, demo ? 1000 : 2400));
        } else {
            // Single-tier octrees and plain .ply scenes have no walk to play —
            // just drop the boot clamp so nothing stays pinned.
            setLodFloor(app.app, 0);
        }
        // Not a gate any more — but "when did it actually finish refining" is
        // exactly the number needed to judge whether revealing early was the
        // right call, so keep measuring it for ?debug=1.
        const settled = waitForSplatSettled(app.app, () => inflightNow === 0, () => lastNetActivityAt)
            .then(() => { marks.settledMs = performance.now() - bootAt; diag.mark('full detail'); recordOnSettle?.(); });
        (window as unknown as Record<string, unknown>).__painted = true;
        // Same fact, as real state: the toolbar's capture gate reads THIS, not
        // window.__painted, which is a test hook and must stay one.
        painted = true;
        stopTracking(); // LOD streams for the scene's whole life — stop counting
        app.app.off('update', onPaceTick);
        const rrFinal = (window as unknown as { __revealReason?: { paint?: string; maxEdges?: number } }).__revealReason;
        if (rrFinal?.paint === 'paint:absolute-cap' && (rrFinal.maxEdges ?? -1) < 6) {
            // Five minutes without one structured frame: dead network or
            // wedged tab. Fading the loader here is what used to paint the
            // grey screen — keep it up with the stalled note instead.
            loadingScreen?.showStalledNote();
        } else if (demo) {
            // Stand demo: the loader closes only once the scene is settled —
            // every tier applied, stream quiet — so the fade never lands on
            // the finest tier's apply (a GPU stall the main-thread meter
            // cannot see, and exactly "freezing right before the scene pops
            // up", Lenny, 13/10/2026). The loader's minimum life (3.2 s)
            // normally covers the settle.
            // BOUNDED. Waiting for settle alone has no floor under it: the
            // gate only lets go on its own when every tier is applied, and
            // on a device where one never is (a tier that fails to load, a
            // stream that never goes quiet) its only other exit is the
            // three-minute cap — the work iPhone sat on black after the
            // animation (Lenny, 13/10/2026). So: settle, or this long after
            // the reveal, whichever is first. A healthy load settles well
            // inside it and sees no difference; a slow one gets the scene
            // refining on screen, as a plain visitor page always has.
            const settleCap = new Promise<void>((resolve) => {
                const t = window.setTimeout(resolve, DEMO_SETTLE_CAP_MS);
                cleanupCallbacks.push(() => window.clearTimeout(t));
            });
            demoReady = Promise.race([settled, settleCap])
                .then(() => restoreRender?.())   // full resolution, drawn behind the loader
                .then(() => loadingScreen?.complete());   // which keeps ≥ 0.5 s before its fade
        } else {
            loadingScreen?.complete();
        }
        // Demo: the adaptive controller can resize the render targets the
        // moment its warm-up ends — a reallocation stall. Let the loader go
        // first (its minimum life plus the fade), then start watching.
        if (demo) {
            const q = quality;
            const t = window.setTimeout(() => q.start(), 5000);
            cleanupCallbacks.push(() => window.clearTimeout(t));
        } else {
            quality.start();
        }

        // Splat reveal + mesh material effects (plays the authored intro once).
        const { splatFx, meshFx } = collectEffects(scene.file);
        if (splatFx.size > 0 || meshFx.size > 0) {
            const stopEffects = applyEffects(app.app, scene.splats, scene.models, splatFx, meshFx);
            cleanupCallbacks.push(stopEffects);
        }

        // Scene title / info panel — top-left. The editor saves the project
        // FILENAME as scene.name ("i5lrl1xoaopz.ssproj" showed up on the first
        // client embed); strip the extension so an un-renamed scene at least
        // does not leak a file suffix to visitors. The real label is set by
        // renaming the scene in the admin dashboard (PATCH /admin/editor-scenes).
        const displayName = scene.file.name?.replace(/\.ssproj$/i, '') || undefined;
        if (ui.info && displayName) {
            new InfoPanel({
                parent: uiRoot,
                info: { title: displayName },
                embed
            });
        }

        // Toolbar — top-right (share / save view / fullscreen).
        // RETAINED and disposed, unlike before: it now holds an app reference,
        // timers, object URLs and an overlay, all of which would survive an
        // editor-preview re-mount if we only relied on container.innerHTML = ''.
        let toolbar: Toolbar | null = null;
        if (ui.toolbar) {
            toolbar = new Toolbar({
                parent: uiRoot,
                canvas,
                container,
                app: app.app,
                sceneName: displayName,
                ready: () => painted,
                demo,
                onScenes: opts.onScenes,
                // The capture is a deliberate multi-hundred-ms hole in the frame
                // stream; without this the FPS window reads it as a slow device
                // and can step quality down a second after the user saved.
                onCapture: () => quality?.resetSamples()
            });
            const tb = toolbar;
            cleanupCallbacks.push(() => tb.dispose());
            // Settings: a cog at the end of the row; Quality for now. The
            // stand demo builds its own below (left of Scenes, or the Q row
            // of its phone menu).
            if (!demo) {
                const settings = new SettingsPanel({ toolbar: tb.element, parent: uiRoot, quality, asQualityRow: true });
                cleanupCallbacks.push(() => settings.dispose());
            }
        }

        // Branding — bottom-left "powered by Olloport".
        if (ui.branding) {
            new Branding({ parent: uiRoot, embed });
        }

        // Markers / annotations — billboarded dots + click popover.
        if (scene.file.markers && scene.file.markers.length > 0) {
            const markers = new MarkersOverlay({
                parent: uiRoot,
                app: app.app,
                cameraEntity: app.cameraEntity,
                markers: scene.file.markers
            });
            cleanupCallbacks.push(() => markers.dispose());
        }

        // Quality dropdown (dev UI, ?quality) — the controller itself was built
        // before the readiness gate above. Only where there is no toolbar to
        // hold the settings cog: with one, visitors pick quality in Settings,
        // and the stand demo's is made with its pills below (the cog left of
        // Scenes, or on a phone the Q row of its menu).
        if ((ui.quality || demo) && !(demo && toolbar)) new QualityDropdown(quality, uiRoot);

        // Nav. Captured at outer scope so the camera tour can pause/resume it.
        let navPause: (() => void) | null = null;
        let navResume: ((pos: Vec3, target: Vec3) => void) | null = null;
        // Re-seat the active controller at a new pose WITHOUT detaching it —
        // used by the editor preview's live camera updates (postMessage) so
        // changing the authored start view doesn't reload the whole splat.
        let navSetPose: ((pos: Vec3, target: Vec3) => void) | null = null;
        // Navigation. Free nav (cursor-look + WASD walk) was removed along with
        // the walk-path feature and is pending a redesign — every scene uses
        // orbit for now. Scenes that request 'free' fall back to orbit.
        const nav = scene.file.navigation ?? 'free';
        const walkCfg = scene.file.walk;
        // Which controller actually got built — NOT scene.file.navigation. A
        // 'free' scene with no boundary silently falls back to orbit below, and
        // the hint has to teach the controls the user really has.
        let hintMode: HintMode = 'orbit';
        // Practice probes for the hint card: a row checks off only when the
        // TAUGHT gesture happened (armed — gesture flags set by the
        // controllers' own input handlers) AND the camera state really moved.
        // Camera change alone is not enough: click-to-glide must not tick
        // "scroll to move", and orbit auto-rotation must not tick "drag".
        let hintProbes: { look: HintProbe; move: HintProbe } | undefined;
        // The controller instance itself, for the A/B/C/D experiment (which
        // needs to reconfigure it). Null only if neither branch ran.
        let navController: WalkController | OrbitController | null = null;
        if (nav !== 'rotating' && walkCfg && (walkCfg.boundary?.length ?? 0) >= 3) {
            hintMode = 'walk';
            // Walk mode: first-person drag-look + click-to-move + WASD, clamped
            // to the authored boundary; camera height from the floor plane.
            const walk = new WalkController(app.cameraEntity, { app: app.app, walk: walkCfg });
            navController = walk;
            // Stand demo on a desktop: no on-screen joystick (Lenny,
            // 13/10/2026) — WASD and the wheel move; the pad is for thumbs.
            // applyScheme() puts it back on every pill, so the pill handler
            // below drops it again.
            // Any desktop since the Nov 2026 controls rework: click, drag and
            // the trackpad cover it, and the guide never mentions a pad.
            if (!isTouchDevice()) walk.setJoystickEnabled(false);
            hintProbes = {
                // look = a real drag rotated the view (~7°+); [yaw, pitch] so
                // a vertical-only drag counts too.
                look: {
                    get: () => {
                        const f = app.cameraEntity.forward;
                        return [Math.atan2(f.x, f.z), Math.asin(Math.max(-1, Math.min(1, f.y)))];
                    },
                    threshold: 0.12,
                    armed: () => walk.gestureLook
                },
                // move = the taught locomotion (WASD or wheel on desktop,
                // joystick on phones) actually travelled. All three flags
                // accepted on either device: the joystick exists on desktop
                // too and is a deliberate move input — only click-glide is
                // excluded, because the card never mentions it.
                move: {
                    get: () => {
                        const p2 = app.cameraEntity.getPosition();
                        return [p2.x, p2.z];
                    },
                    threshold: 0.35,
                    armed: () => walk.gestureWheel || walk.gestureKeys || walk.gestureJoystick ||
                        walk.gestureHold || walk.gestureTwoFinger
                }
            };
            const initial = scene.file.camera?.initial;
            if (initial) {
                const ip = initial.position;
                const it = initial.target ?? [ip[0], ip[1], ip[2] - 1];
                walk.initFromPose(new Vec3(ip[0], ip[1], ip[2]), new Vec3(it[0], it[1], it[2]));
                if (typeof initial.fov === 'number' && app.cameraEntity.camera) {
                    app.cameraEntity.camera.fov = initial.fov;
                    // Match the editor's axis: it stores fov on the LARGER
                    // viewport axis (horizontalFov = width > height). PlayCanvas
                    // defaults to vertical fov, so on a wide canvas the same
                    // number reads as a much wider view — the "fov looks
                    // different" mismatch. Mirror the editor's rule here.
                    app.cameraEntity.camera.horizontalFov = canvas.clientWidth >= canvas.clientHeight;
                }
            }
            walk.attach(canvas);
            // Voxel collision streams in after the splat; until it lands the
            // camera walks the planes, as in a scene without voxels.
            if (walkCfg.voxels?.url) {
                let gone = false;
                cleanupCallbacks.push(() => { gone = true; });
                const voxelsUrl = new URL(walkCfg.voxels.url, scene.baseUrl).toString();
                const voxelsTransform = walkCfg.voxels.transform;
                loadVoxelGrid(voxelsUrl)
                    .then((grid) => { if (!gone) walk.setVoxels(new VoxelCollider(grid, voxelsTransform)); })
                    .catch((err) => console.warn('viewer2: collision voxels failed to load — walking the planes only', err));
            }
            const onUpdate = (dt: number) => walk.update(dt);
            app.app.on('update', onUpdate);
            (window as unknown as Record<string, unknown>).__nav = walk;
            cleanupCallbacks.push(() => { walk.detach(); app.app.off('update', onUpdate); });
            navPause = () => { walk.detach(); app.app.off('update', onUpdate); };
            navResume = (pos: Vec3, target: Vec3) => {
                walk.initFromPose(pos, target);
                walk.attach(canvas);
                app.app.on('update', onUpdate);
            };
            navSetPose = (pos: Vec3, target: Vec3) => walk.initFromPose(pos, target);
        } else {
            if (nav !== 'rotating') {
                console.warn('viewer2: navigation "free" but no walk boundary (>=3 pts) — using orbit');
            }
            // Orbit mode pivot selection:
            //  - If the author deliberately framed a starting view
            //    (camera.initial.explicit), orbit around THAT look-target — so
            //    "Set to current view" reproduces the exact entry shot and
            //    spins around what the author was looking at.
            //  - Otherwise pivot = first splat's transform.position (the editor
            //    centres each splat's origin in its content), falling back to
            //    scene centre. An UNflagged camera.initial.target is ignored on
            //    purpose: a live-viewport/legacy target can sit metres off
            //    (cartoixa's 15m offset) and would orbit empty space.
            // In both cases camera.initial.position drives the starting angle +
            // distance via initFromPose.
            const orbit = new OrbitController(app.cameraEntity);
            navController = orbit;
            const firstSplat = scene.file.splats?.[0];
            const splatPos = firstSplat?.transform?.position;
            const initial = scene.file.camera?.initial;
            // Pivot: honor the authored look-target when the author flagged it
            // (Set to current view) OR when the scene is EXPLICITLY 'rotating'
            // — a deliberate orbit scene's saved framing IS the intended pivot.
            // The legacy 'explicit' guard only protects the free-fallback path,
            // where an unframed target can sit metres off the splat.
            const trustTarget = (nav === 'rotating' || initial?.explicit) && initial?.target;
            const authoredTarget = trustTarget
                ? new Vec3(initial.target[0], initial.target[1], initial.target[2])
                : null;
            const pivot = authoredTarget
                ?? (splatPos
                    ? new Vec3(splatPos[0], splatPos[1], splatPos[2])
                    : scene.sceneCenter.clone());
            if (initial) {
                orbit.initFromPose(
                    new Vec3(initial.position[0], initial.position[1], initial.position[2]),
                    pivot
                );
                if (typeof initial.fov === 'number' && app.cameraEntity.camera) {
                    app.cameraEntity.camera.fov = initial.fov;
                    // See note above: match the editor's larger-axis fov rule so
                    // the framing matches what was authored.
                    app.cameraEntity.camera.horizontalFov = canvas.clientWidth >= canvas.clientHeight;
                }
            } else {
                orbit.target.copy(pivot);
                orbit.distance = scene.sceneRadius * 1.5;
                orbit.azimuth = Math.PI / 4;
                orbit.elevation = Math.PI / 6;
            }
            // Per-scene orbit behavior from scene.json camera.orbit, with
            // polished defaults for explicit "rotating" (drone/exterior)
            // scenes: pitch floor 16° (no under-ground views), zoom clamped to
            // 0.5–2× scene radius, idle auto-rotation on. The "free"-fallback
            // (indoor scenes without a walk block) keeps loose limits and no
            // auto-rotation unless the scene explicitly opts in.
            // Radius for the zoom band: prefer the EDITOR-measured value
            // stamped into camera.orbit at save time — it's the exact number
            // the capture-from-view buttons divided by. Recomputing here can
            // disagree (world vs local bounds under rotation/scale) and put
            // the authored limits at the wrong distances.
            const orbitCfg = scene.file.camera?.orbit;
            const polishRadius = orbitCfg?.sceneRadius ?? scene.sceneRadius;
            const isRotating = nav === 'rotating';
            const applyOrbitPolish = () => {
                const pitchMinDeg = orbitCfg?.pitchMinDeg ?? (isRotating ? 16 : null);
                if (pitchMinDeg !== null) orbit.minElevation = (pitchMinDeg * Math.PI) / 180;
                // Authored zoom limits in WORLD METERS take precedence — used
                // verbatim, no radius math (bounding radius is unreliable on
                // drone scenes with outlier floaters). Legacy ×radius factors
                // remain as the fallback for older scenes / defaults.
                const zoomMinM = (orbitCfg?.zoomMinM ?? 0) > 0 ? orbitCfg!.zoomMinM! : null;
                const zoomMaxM = (orbitCfg?.zoomMaxM ?? 0) > 0 ? orbitCfg!.zoomMaxM! : null;
                if (zoomMinM !== null) {
                    orbit.minDistance = zoomMinM;
                } else {
                    const zoomMinF = orbitCfg?.zoomMinFactor ?? (isRotating ? 0.5 : null);
                    if (zoomMinF !== null) orbit.minDistance = Math.max(0.05, polishRadius * zoomMinF);
                }
                if (zoomMaxM !== null) {
                    orbit.maxDistance = Math.max(orbit.minDistance * 1.05, zoomMaxM);
                } else {
                    const zoomMaxF = orbitCfg?.zoomMaxFactor ?? (isRotating ? 2 : null);
                    if (zoomMaxF !== null) orbit.maxDistance = Math.max(orbit.minDistance * 1.2, polishRadius * zoomMaxF);
                }
                orbit.autoRotate = orbitCfg?.autoRotate ?? isRotating;
                if (typeof orbitCfg?.autoRotateSpeed === 'number') {
                    orbit.autoRotateSpeed = (orbitCfg.autoRotateSpeed * Math.PI) / 180;
                }
                if (typeof orbitCfg?.autoRotateDelay === 'number') {
                    orbit.autoRotateDelay = orbitCfg.autoRotateDelay;
                }
            };
            orbit.fitToBounds(scene.sceneRadius);
            applyOrbitPolish();
            // Practice probes AFTER the polish so the zoom threshold reads
            // the FINAL clamp band. It must come from the ACHIEVABLE zoom
            // range, not the raw scene radius: drone scenes carry
            // outlier-inflated radii (cartoixa reports 1776m) while the
            // authored band is a few meters — 3% of radius (53m) could never
            // be reached, so the zoom tick never fired. 15% of the band,
            // capped at roughly one wheel notch of the current distance, is
            // one or two real notches of zooming.
            hintProbes = {
                look: {
                    get: () => [orbit.azimuth],
                    threshold: 0.08,
                    // Idle auto-rotation moves azimuth on its own — only real
                    // input (drag, WASD/margin/scroll steering) ticks this.
                    armed: () => orbit.gestureRotate || orbit.gestureKeys || orbit.gestureEdge || orbit.gestureScroll
                },
                move: {
                    get: () => [orbit.distance],
                    threshold: Math.max(0.1, Math.min(
                        (orbit.maxDistance - orbit.minDistance) * 0.15,
                        orbit.distance * 0.08
                    )),
                    armed: () => orbit.gestureZoom
                }
            };
            orbit.attach(canvas);
            const onUpdate = (dt: number) => orbit.update(dt);
            app.app.on('update', onUpdate);
            (window as unknown as Record<string, unknown>).__nav = orbit;
            cleanupCallbacks.push(() => {
                orbit.detach();
                app.app.off('update', onUpdate);
            });
            // Tour pause/resume: detach orbit input + update during the tour,
            // then re-seed it from the camera's final pose so the user can
            // keep orbiting from where the tour ended.
            navPause = () => { orbit.detach(); app.app.off('update', onUpdate); };
            navResume = (pos: Vec3, _target: Vec3) => {
                // IGNORE the passed target: the tour hands us a synthetic point
                // 5m in front of wherever the camera stopped, and seeding the
                // orbit on it makes the scene orbit empty space after a
                // mid-tour stop. Keep the SCENE pivot; only the angles/distance
                // re-derive from the camera's current position.
                orbit.initFromPose(pos, pivot);
                orbit.attach(canvas);
                app.app.on('update', onUpdate);
            };
            const radius = scene.sceneRadius;
            navSetPose = (pos: Vec3, target: Vec3) => {
                orbit.initFromPose(pos, target);
                orbit.fitToBounds(radius);
                applyOrbitPolish(); // fitToBounds resets the zoom band — reapply
            };
        }

        // Measure tool: ruler in the toolbar, two taps = a distance. Needs the
        // toolbar for its button, so a chrome-less embed gets none. Not on the
        // stand demo: its toolbar row is Quality and Scenes only (13/10/2026).
        let measureTool: MeasureTool | null = null;
        if (toolbar && !demo) {
            const walkCtl = navController instanceof WalkController ? navController : null;
            const surfaces = walkCfg ? buildWalkSurfaces(walkCfg) : [];
            let clickToMoveBefore = true;
            let dragLookBefore = true;
            const measure = measureTool = new MeasureTool({
                parent: uiRoot,
                canvas,
                app: app.app,
                cameraEntity: app.cameraEntity,
                splats: () => scene?.splats.values() ?? [],
                // Glossy floors scan as a mirrored room under the floor; the
                // authored walk floors stop the ray where the floor really is.
                floorPick: surfaces.length === 0 ? undefined : (ro, rd) => {
                    const r = raycastWalkSurfaces([ro.x, ro.y, ro.z], [rd.x, rd.y, rd.z], surfaces);
                    return r ? new Vec3(r.point[0], r.point[1], r.point[2]) : null;
                },
                voxelPick: (ro, rd) => {
                    const vox = walkCtl?.collisionVoxels;
                    const t = vox ? vox.raycast([ro.x, ro.y, ro.z], [rd.x, rd.y, rd.z], 200) : NaN;
                    return Number.isNaN(t) ? null : new Vec3(ro.x + rd.x * t, ro.y + rd.y * t, ro.z + rd.z * t);
                },
                onActiveChange: (on) => {
                    if (!walkCtl) return;
                    if (on) {
                        clickToMoveBefore = walkCtl.clickToMoveEnabled;
                        walkCtl.setClickToMoveEnabled(false);
                    } else {
                        walkCtl.setClickToMoveEnabled(clickToMoveBefore);
                    }
                },
                // A finger aiming with the magnifier must not swing the view.
                onAimChange: (on) => {
                    if (!walkCtl) return;
                    if (on) {
                        dragLookBefore = walkCtl.dragLookEnabled;
                        walkCtl.setDragLookEnabled(false);
                    } else {
                        walkCtl.setDragLookEnabled(dragLookBefore);
                    }
                }
            });
            measure.bindButton(toolbar.addButton(ICON_RULER, measure.label, () => measure.toggle()));
            cleanupCallbacks.push(() => measure.dispose());
        }

        // Controls hint — teaches THIS scene's navigation, then dismisses
        // itself. After the nav branches so it reflects the controller that was
        // actually built. A function rather than a statement because the
        // experiment below re-runs it on every scheme change: pick a scheme,
        // get the tutorial for that scheme.
        // Read out here, not inside the closure: `scene` is a nullable outer
        // `let`, and TS drops its narrowing across a function boundary.
        const tourPoseCount = scene.file.camera?.poses?.length ?? 0;
        // Stand demo: the step-by-step tutorial (ui/demo-tutorial.ts) is the
        // only teaching, on every device — no hint card in the demo.
        const tutorialWanted = demo &&
            (navController instanceof WalkController || navController instanceof OrbitController);
        // The tour's ▶, built further down; the tutorial's last bubble reads
        // it when that step runs.
        let tourButton: HTMLElement | null = null;
        // Phone: the cog menu; the tour's ▶ moves into it once built.
        let demoMenu: DemoMenu | null = null;
        let hint: ControlsHint | null = null;
        // Walk scenes: the step-by-step onboarding guide replaces the small
        // card (user tests, Oct 2026: first-timers didn't get the card). It
        // runs once per device; the toolbar's ? replays it.
        let guide: OnboardingGuide | null = null;
        const walkNav = navController instanceof WalkController ? navController : null;
        const startGuide = () => {
            if (!walkNav) return;
            guide?.dispose();
            hint?.dispose();
            hint = null;
            // The measuring tip waits for the guide, then follows it.
            measureTool?.holdTip();
            guide = new OnboardingGuide({
                parent: uiRoot,
                walk: walkNav,
                camera: app.cameraEntity,
                touch: isTouchDevice(),
                onClose: () => { guide = null; measureTool?.releaseTip(); }
            });
        };
        cleanupCallbacks.push(() => guide?.dispose());
        if (walkNav && toolbar && ui.hints && !demo) {
            toolbar.addButton(ICON_HELP, GUIDE_LABEL[resolveLang()], startGuide, GUIDE_SHORT[resolveLang()])
                .classList.add('ollo-help-btn');
        }
        // `rows` belongs to the experiment: a scheme whose gestures the card's
        // own mode × device copy can't describe hands it rows. Undefined =
        // the shipped card.
        const showHint = (rows?: HintRow[]) => {
            if (!ui.hints) return;
            if (walkNav && !rows) {
                if (OnboardingGuide.wanted()) startGuide();
                return;
            }
            hint?.dispose();
            hint = new ControlsHint({
                parent: uiRoot,
                mode: hintMode,
                probes: hintProbes,
                rows,
                // The tour's ▶ shares the bottom-centre slot.
                aboveTour: tourPoseCount > 0
            });
        };
        cleanupCallbacks.push(() => hint?.dispose());

        // Set by the camera tour below while it owns the camera. The picker
        // must not hand navigation back to the controller mid-flight.
        let tourPlaying = false;

        // ── A/B/C/D control-scheme experiment ─────────────────────────────
        // Parallel layer (src/experiments/): a panel offers movement schemes,
        // teaches the chosen one, and lives behind a cog in the toolbar. Two
        // modes — 'prompt' blocks on load (testing), 'cog' starts the viewer
        // exactly as shipped with the cog available (production). Off (?ab=0,
        // or DEFAULT_MODE = 'off'), or not eligible (a phone), this branch
        // never runs and the hint fires exactly as it always has. To remove it for good: delete src/experiments/, this
        // block, showHint's `rows` parameter, and the navController/
        // tourPlaying bookkeeping it reads.
        const schemeModeNow = schemeMode();
        if (schemeModeNow !== 'off' && navController && schemesEligible(hintMode)) {
            const touch = isTouchDevice();
            const registry = registryFor(hintMode, touch);
            const ctx: SchemeContext = { nav: navController, kind: hintMode, touch, canvas };
            const feedbackUrl = feedbackEndpoint(sceneUrl);
            const picker = new SchemePicker({
                parent: uiRoot,
                registry,
                prompt: schemeModeNow === 'prompt',
                // The panel's backdrop stops pointer input by covering the
                // canvas, but the keyboard walk (window listeners) and the
                // virtual joystick (a sibling of the ui root that paints
                // ABOVE its stacking context) are out of its reach — detaching
                // the controller is what actually silences those.
                onOpen: () => { if (!tourPlaying) navPause?.(); },
                onClose: () => {
                    if (tourPlaying) return;
                    const p = app.cameraEntity.getPosition().clone();
                    const f = app.cameraEntity.forward;
                    // Re-seat at the pose we paused on: a point 1m along the
                    // current forward reproduces the exact yaw/pitch for walk,
                    // and orbit ignores the target and keeps its own pivot.
                    navResume?.(p, new Vec3(p.x + f.x, p.y + f.y, p.z + f.z));
                },
                onChoose: (id) => { applyScheme(id, ctx); showHint(schemeHintRows(id, registry)); },
                onFeedback: feedbackUrl ? (answers) => sendFeedback(feedbackUrl, answers, sceneUrl) : undefined
            });
            cleanupCallbacks.push(() => picker.dispose());
            if (schemeModeNow === 'cog') {
                // No prompt: the control arm is in force and the card teaches
                // it, as shipped. The cog is the only thing that's new.
                applyScheme(DEFAULT_SCHEME, ctx);
                if (!tutorialWanted) showHint();
            }
        } else if (!tutorialWanted) {
            showHint();
        }

        // Stand demo: a plain row of the schemes picked for the stand
        // (ui/demo-controls.ts lists them per scene kind and device; a touch
        // orbit scene has none) and the tutorial. Same applyScheme() as the
        // experiment, no picker, no feedback form.
        if (demo && navController) {
            const touch = isTouchDevice();
            // A phone (Lenny, 14/10/2026): no room for the pills in the top
            // row, so a cog in the toolbar opens a menu with the schemes,
            // the flythrough and the quality. iPad and desktop keep the row.
            const phone = touch && isPhoneScreen() && !!toolbar;
            const demoChoices = demoChoicesFor(hintMode, touch);
            const ctx: SchemeContext = { nav: navController, kind: hintMode, touch, canvas };
            let tutorial: DemoTutorial | null = null;
            const choose = (id: SchemeId) => {
                applyScheme(id, ctx);
                if (!touch && navController instanceof WalkController) navController.setJoystickEnabled(false);
                // The tutorial is the only teaching. Its flow for the
                // scheme runs if there is anything left of it; if not
                // (seen already, or skipped), nothing shows — no hint
                // card, on any device (Lenny, 13/10/2026).
                tutorial?.start(id);
            };
            const pills = demoChoices && !phone ? new DemoControls({
                parent: uiRoot,
                choices: demoChoices,
                initial: DEFAULT_SCHEME,
                onChoose: choose
            }) : null;
            if (pills) cleanupCallbacks.push(() => pills.dispose());
            if (phone && toolbar) {
                const menu = new DemoMenu({
                    toolbar: toolbar.element,
                    before: toolbar.scenesBtn,
                    parent: uiRoot,
                    choices: demoChoices,
                    initial: DEFAULT_SCHEME,
                    onChoose: choose,
                    onOpen: () => tutorial?.menuOpened(),
                    onClose: () => tutorial?.menuClosed()
                });
                demoMenu = menu;
                cleanupCallbacks.push(() => menu.dispose());
            }
            // Quality: the Q row of the phone menu, else the same settings
            // cog as the viewer's, left of Scenes (13/10/2026, 04/11/2026).
            if (toolbar && quality) {
                if (demoMenu) demoMenu.qualitySlot.appendChild(new QualityPicker(quality).element);
                else {
                    const settings = new SettingsPanel({ toolbar: toolbar.element, before: toolbar.scenesBtn, parent: uiRoot, quality });
                    cleanupCallbacks.push(() => settings.dispose());
                }
            }
            if (tutorialWanted) {
                // The controller's set-once gesture flags, by scene kind: walk
                // = look + (keys or wheel; on touch the joystick, or the hold
                // under Hold to walk); orbit = rotate + zoom (wheel under
                // Classic, W/S under Gamer, the pinch on touch — all raise
                // gestureZoom).
                const nc = navController;
                const gestures: TutorialGestures = nc instanceof WalkController
                    ? {
                        look: () => {
                            const f = app.cameraEntity.forward;
                            return [Math.atan2(f.x, f.z), Math.asin(Math.max(-1, Math.min(1, f.y)))];
                        },
                        lookArmed: () => nc.gestureLook,
                        resetLook: () => { nc.gestureLook = false; },
                        moveArmed: () => nc.gestureKeys || nc.gestureWheel,
                        resetMove: () => { nc.gestureKeys = false; nc.gestureWheel = false; },
                        joystickArmed: () => nc.gestureJoystick,
                        resetJoystick: () => { nc.gestureJoystick = false; },
                        holdArmed: () => nc.gestureHold,
                        resetHold: () => { nc.gestureHold = false; }
                    }
                    : {
                        look: () => [nc.azimuth, nc.elevation],
                        lookArmed: () => nc.gestureRotate,
                        resetLook: () => { nc.gestureRotate = false; },
                        moveArmed: () => nc.gestureZoom,
                        resetMove: () => { nc.gestureZoom = false; }
                    };
                // The pill the "another way" bubble points at: Gamer on
                // desktop (E on a walk scene, G on an orbit one), Hold to
                // walk (C) on touch.
                const altPills = touch ? ['C' as const] : ['E' as const, 'G' as const];
                tutorial = new DemoTutorial({
                    parent: uiRoot,
                    kind: hintMode,
                    touch,
                    gestures,
                    anchors: {
                        schemes: altPills.map(id => pills?.button(id) ?? null).filter((b): b is HTMLButtonElement => !!b),
                        scenes: toolbar?.scenesBtn ?? null,
                        // Phone: the ▶ sits inside the menu, the cog bubble covers it.
                        tour: () => (demoMenu ? null : tourButton),
                        settings: demoMenu?.cog ?? null,
                        menu: demoMenu?.panel ?? null
                    }
                });
                tutorial.start(DEFAULT_SCHEME);
                const tut = tutorial;
                cleanupCallbacks.push(() => tut.dispose());
            }
        }

        // Live control from an embedding editor (the "Preview in viewer" loop).
        // Lets the editor jump the camera to a freshly-authored start view, and
        // pause rendering while the preview overlay is hidden — both WITHOUT
        // reloading the (potentially huge) splat. Dev/preview convenience; a
        // standalone viewer simply never receives these messages.
        const onEditorMessage = (e: MessageEvent) => {
            const d = e.data as { type?: string; position?: number[]; target?: number[]; fov?: number; active?: boolean };
            if (!d || typeof d.type !== 'string') return;
            if (d.type === 'olloport:setCamera') {
                if (!Array.isArray(d.position) || !Array.isArray(d.target)) return;
                const pos = new Vec3(d.position[0], d.position[1], d.position[2]);
                const target = new Vec3(d.target[0], d.target[1], d.target[2]);
                const cam = app.cameraEntity.camera;
                if (typeof d.fov === 'number' && cam) {
                    cam.fov = d.fov;
                    cam.horizontalFov = canvas.clientWidth >= canvas.clientHeight;
                }
                if (navSetPose) {
                    navSetPose(pos, target);
                } else {
                    app.cameraEntity.setPosition(pos);
                    app.cameraEntity.lookAt(target);
                }
                app.app.renderNextFrame = true;
            } else if (d.type === 'olloport:setActive') {
                // Stop the render loop while hidden so two heavy renderers
                // (editor + preview) don't fight for the GPU; the scene stays
                // in memory so re-showing is instant.
                app.app.autoRender = d.active !== false;
                if (d.active !== false) app.app.renderNextFrame = true;
            }
        };
        window.addEventListener('message', onEditorMessage);
        cleanupCallbacks.push(() => window.removeEventListener('message', onEditorMessage));

        // Camera tour — play button that flies through camera.poses[].
        const poses = scene.file.camera?.poses;
        if (poses && poses.length > 0) {
            const tour = new CameraTour({
                parent: uiRoot,
                app: app.app,
                cameraEntity: app.cameraEntity,
                poses,
                onPlayStart: () => { tourPlaying = true; navPause?.(); },
                onPlayEnd: (pos, target) => { tourPlaying = false; navResume?.(pos, target); }
            });
            tourButton = tour.playButton;
            demoMenu?.adoptTour(tour.playButton);
            cleanupCallbacks.push(() => tour.dispose());

            // Stand demo: a flythrough recorded on this device for this
            // orientation plays instead of the live one. None, or stale →
            // the live flythrough, as before.
            if (demo && !opts.record) {
                let disposed = false;
                let objectUrl: string | null = null;
                cleanupCallbacks.push(() => {
                    disposed = true;
                    if (objectUrl) URL.revokeObjectURL(objectUrl);
                });
                let modeBefore: QualityMode | null = null;
                // One video per orientation, shown cropped to fill any window
                // of it. When the window turns (an iPad rotated) the lookup is
                // redone: the other orientation's video swaps in, or none and
                // ▶ flies live. Never while a tour is playing.
                let resizeTimer = 0;
                let lookedUp: Orientation | null = null;
                const refreshVideo = () => {
                    if (tourPlaying) return;
                    lookedUp = orientation();
                    void flythroughUrl(sceneUrl).then((found) => {
                        if (disposed || tourPlaying) { if (found) URL.revokeObjectURL(found.url); return; }
                        const old = objectUrl;
                        objectUrl = found?.url ?? null;
                        tour.setVideo(found ? {
                            url: found.url,
                            after: canvas,
                            settle: () => waitViewSettled(app.app, { capMs: 3000 }),
                            // The video was recorded on the top rung; the live
                            // view it fades onto must be drawn on it too.
                            hold: () => { modeBefore = quality?.currentMode ?? null; quality?.setMode('high'); },
                            release: () => { if (modeBefore) quality?.setMode(modeBefore); modeBefore = null; }
                        } : null);
                        if (old) URL.revokeObjectURL(old);
                    });
                };
                const onWindowResize = () => {
                    if (orientation() === lookedUp) return;
                    // Until the new lookup lands, the old video is wrong.
                    if (!tourPlaying) tour.setVideo(null);
                    window.clearTimeout(resizeTimer);
                    resizeTimer = window.setTimeout(refreshVideo, 400);
                };
                window.addEventListener('resize', onWindowResize);
                cleanupCallbacks.push(() => { window.removeEventListener('resize', onWindowResize); window.clearTimeout(resizeTimer); });
                refreshVideo();
            }

            const rec = opts.record;
            if (rec) {
                void (async () => {
                    try {
                        await (demoReady ?? Promise.resolve());
                        // The loader's fade.
                        await new Promise(res => setTimeout(res, 800));
                        navPause?.();
                        uiRoot.style.visibility = 'hidden';
                        quality?.setMode('high');
                        const result = await recordFlythrough({
                            app: app.app,
                            canvas,
                            sceneUrl,
                            duration: tour.duration,
                            applyAt: (t) => tour.applyAt(t)
                        }, rec.onProgress, rec.signal);
                        rec.onDone(null, result);
                    } catch (err) {
                        rec.onDone(err);
                    }
                })();
            }
        } else if (opts.record) {
            opts.record.onDone(new Error('this scene has no flythrough'));
        }

        // VR — the button only appears on devices that can run immersive VR.
        if (app.app.xr) {
            const vr = new VrMode({
                viewer: app,
                parent: uiRoot,
                walk: walkCfg && (walkCfg.boundary?.length ?? 0) >= 3 ? walkCfg : undefined,
                onEnter: () => navPause?.(),
                onExit: (pos, target) => navResume?.(pos, target),
                pauseQuality: () => quality?.stop(),
                resumeQuality: () => quality?.start()
            });
            cleanupCallbacks.push(() => vr.dispose());
        }
    } catch (err) {
        console.error('viewer2: scene load failed', err);
        void restoreRender?.();
        loadingScreen?.finish();
    }

    return {
        app,
        scene,
        quality,
        destroy: () => {
            // The engine decodes SOG chunks on the GPU and reads the result
            // back asynchronously (readImageDataAsync); a read still in
            // flight when the device goes rejects with "Texture read did not
            // complete, as the graphics device was destroyed", unhandled
            // inside the engine — and only when the fenced read finally
            // resolves, which was measured at 7 s after the destroy, with
            // the next scene already loading. That is teardown noise, not a
            // fault (the device is being destroyed on purpose), so that one
            // message is swallowed for the rest of the page's life. Seen on
            // the stand demo's Scenes button after a settled reveal
            // (12/10/2026).
            installTeardownNoiseFilter();
            for (const fn of cleanupCallbacks) fn();
            quality?.stop();
            scene?.dispose();
            // The engine's device destroy drops its reference to the WebGL
            // context but never loses it, so every open/close of a scene
            // left a live context behind until the canvas was collected —
            // Chrome allows sixteen, then starts killing the oldest, and a
            // GPU process under that kind of pressure is the one that ends
            // up refusing WebGL altogether (13/10/2026). Lose it on purpose.
            const gl = (app.graphicsDevice as unknown as { gl?: WebGLRenderingContext | null }).gl ?? null;
            app.destroy();
            try { gl?.getExtension('WEBGL_lose_context')?.loseContext(); } catch { /* already gone */ }
            container.innerHTML = '';
            container.classList.remove('viewer-mount');
        }
    };
}
